import dayjs from "dayjs";

import { describeError } from "../../common/rateLimit";

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

/**
 * One member's ban state — a row of `bannedUsers`, and what `setUserBanned`
 * returns.
 *
 * Deliberately a small projection of the user row rather than the full `User`
 * type: this is the only place the API hands a *deleted* account back to the
 * dashboard, and it carries only what the ban screens need.
 */
export interface UserBanStatus {
  userId: number;
  /** As stored. A ban does not scrub the row, so this is their real username. */
  username: string;
  email?: string | null;
  phone?: string | null;
  /** The toggle. True while the ban is in force. */
  isBanned: boolean;
  /**
   * Whether the account also reads as deleted — the flag that actually stops it
   * signing in and registering again.
   *
   * Normally tracks `isBanned`. The two come apart when the member had already
   * deleted their own account: un-banning leaves this true, because that
   * deletion was theirs to keep.
   */
  isDeleted: boolean;
  /**
   * True when the row was already scrubbed by a self-deletion — no email, no
   * phone, a rewritten login identity. Banning one changes nothing they could
   * do, and un-banning cannot restore the account.
   */
  isAnonymized: boolean;
  bannedAt?: string | null;
  banReason?: string | null;
  /** The admin who banned them, as a name support will recognise. */
  bannedBy?: string | null;
  /** Set by the mutation when the outcome needs explaining. Never by the list. */
  warning?: string | null;
}

export interface BannedUserList {
  users: Array<UserBanStatus>;
  /** Banned accounts in total, ignoring both pagination and the search filter. */
  total: number;
}

export interface UserBanSettings {
  /** Whether `BanGuard` is rejecting banned callers. Not whether banning works. */
  enabled: boolean;
  /** Accounts currently banned, however the switch is set. */
  bannedCount: number;
  /** Last write to the switch. The only audit signal the row has. */
  updatedAt?: string | null;
  /** True while the row has never been written and the compiled default applies. */
  usingDefault: boolean;
}

/**
 * The minimum needed to ban somebody, so the Users list can open the dialog
 * from a row it fetched through `fetchUsers` rather than through `bannedUsers`.
 */
export interface BanTarget {
  userId: number;
  username: string;
  email?: string | null;
  phone?: string | null;
  /** Current state, which decides whether the dialog bans or un-bans. */
  isBanned: boolean;
  /** Unknown from a `fetchUsers` row; only `bannedUsers` reports it. */
  isAnonymized?: boolean;
}

/* ------------------------------------------------------------------ *
 * Standing copy
 * ------------------------------------------------------------------ */

/**
 * `BanGuard` holds the banned set for 30s per instance. The instance that served
 * the write drops its copy immediately, the others catch up within the window —
 * in both directions, so never claim either a ban or an un-ban is instant
 * everywhere.
 */
export const PROPAGATION_NOTICE =
  "In force on the server that handled it straight away, and everywhere else within about 30 seconds.";

/**
 * What the enforcement switch actually governs.
 *
 * The trap this exists to head off: the switch reads like "banning on/off", and
 * an admin turning it off to give somebody access back would achieve nothing
 * visible to that person — the lockout is `isDeleted`, which the switch does not
 * touch.
 */
export const ENFORCEMENT_SCOPE_NOTICE =
  "This governs the global guard that refuses a banned caller on every operation — including the unguarded and public ones. It is not an amnesty: a ban also marks the account deleted, and sign-in, the verification-code paths and re-registration all key off that flag, which this switch does not touch. To give one person their account back, un-ban them.";

/** Why a reason is worth typing, given nobody but staff ever reads it. */
export const REASON_HINT =
  "Stored on the account and read by whoever picks up the support ticket. The member never sees it.";

/**
 * Copy-pasteable proof of why somebody never got their code.
 *
 * The OTP paths refuse a banned identity **silently** — no message sent, no
 * error raised — because a distinguishable error there would be an "is this
 * number registered" oracle. The audit row is the only place the reason exists,
 * and `outcome` is what separates a ban from a self-deletion.
 */
export const OTP_SEND_LOG_SQL = `SELECT "createdAt", channel, destination, "ipAddress", outcome
FROM "OtpSendLog"
WHERE outcome IN ('banned_account', 'deleted_account')
ORDER BY "createdAt" DESC LIMIT 50;`;

/* ------------------------------------------------------------------ *
 * Identity
 * ------------------------------------------------------------------ */

/**
 * The contact details on the row, or why there are none.
 *
 * An anonymized row is reported as erased rather than left blank: "no email" on
 * an account that had one until the member deleted it would read as missing
 * data.
 *
 * Takes the three fields it reads rather than a whole status, so the ban dialog
 * can call it with a `BanTarget` from the Users list.
 */
export function describeContact(user: {
  email?: string | null;
  phone?: string | null;
  isAnonymized?: boolean | null;
}): string {
  if (user.isAnonymized) {
    return "Email, phone and login identity erased by the member's own deletion";
  }

  const parts = [user.email, user.phone].filter(Boolean) as Array<string>;

  return parts.length ? parts.join(" · ") : "No email or phone on the account";
}

export function formatBanTimestamp(value: string | null | undefined): string {
  if (!value) return "";

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("D MMM YYYY, HH:mm") : "";
}

/**
 * The audit line for one banned row.
 *
 * `bannedBy` is null for a ban applied by a hand-edit of the table, so an absent
 * name is reported as unknown rather than omitted — "banned by nobody" would be
 * misleading, and this column is the only place the actor is recorded.
 */
export function describeBannedSince(user: UserBanStatus): string {
  if (!user.isBanned) return "—";

  const when = formatBanTimestamp(user.bannedAt);
  const who = user.bannedBy ? `by ${user.bannedBy}` : "by an unknown admin";

  return when ? `${when} · ${who}` : who;
}

export function describeBanReason(user: UserBanStatus): string {
  return user.banReason?.trim() || "No reason recorded";
}

/* ------------------------------------------------------------------ *
 * Lockout state — what the ban is actually doing to this account
 * ------------------------------------------------------------------ */

export type LockoutState = "locked-out" | "self-deleted" | "not-locked-out";

/**
 * Which of the three states a row is in, which is not the same question as
 * `isBanned`.
 *
 * - `locked-out` — the ban set `isDeleted`, so every guarded operation, sign-in
 *   and re-registration turns them away. The normal banned row.
 * - `self-deleted` — the member had already deleted the account. The ban is
 *   recorded, but there was nothing left to lock out, and un-banning cannot
 *   bring it back.
 * - `not-locked-out` — no lockout applied. Either they are not banned, or the
 *   flag was cleared behind the feature's back (see `isHalfApplied`).
 */
export function lockoutStateOf(user: UserBanStatus): LockoutState {
  if (user.isAnonymized) return "self-deleted";

  return user.isDeleted ? "locked-out" : "not-locked-out";
}

/**
 * Marked banned, but not locked out — so only the global guard is turning them
 * away, and the enforcement switch can stop that.
 *
 * Nothing in this dashboard produces the state; a direct edit of the table does.
 * It matters because it is invisible otherwise: the row says "banned" and the
 * person can still sign in.
 */
export function isHalfApplied(user: UserBanStatus): boolean {
  return user.isBanned && !user.isDeleted && !user.isAnonymized;
}

/** The chip on a banned row — the answer to "is this ban doing anything?". */
export function describeLockout(user: UserBanStatus): string {
  switch (lockoutStateOf(user)) {
    case "locked-out":
      return "Locked out";
    case "self-deleted":
      return "Was already deleted";
    default:
      return user.isBanned ? "Not locked out" : "Active";
  }
}

/**
 * The fix for a half-applied ban, which is not "ban them again".
 *
 * Re-applying a ban the account already has returns early on the server and
 * changes nothing, so the lockout has to be lifted and re-taken.
 */
export const HALF_APPLIED_HINT =
  "This account is marked banned but not locked out, so only the global guard is refusing it — and Ban enforcement can switch that off. Re-applying the ban does nothing (the server ignores a state the account already has): un-ban them, then ban them again.";

/* ------------------------------------------------------------------ *
 * Writing
 * ------------------------------------------------------------------ */

/**
 * What un-banning this person will actually do, for the confirmation.
 *
 * Three cases, because whether the row was anonymized is **not always known**:
 * `bannedUsers` reports it, `fetchUsers` has no field for it, and the server's own
 * test is a uid pattern this dashboard must not reimplement — a copy of that
 * heuristic would drift and start claiming the wrong thing. So an unknown row
 * gets wording that does not promise a restore it may not deliver, and the
 * server's `warning` settles it afterwards.
 */
export function describeUnbanEffect(target: BanTarget): {
  warns: boolean;
  text: string;
} {
  if (target.isAnonymized === true) {
    return {
      warns: true,
      text: "This member had already deleted the account themselves. Its email, phone and login identity were erased at that point, so the account stays deleted and cannot be signed into. Lifting the ban only clears the ban itself.",
    };
  }

  if (target.isAnonymized === undefined) {
    return {
      warns: false,
      text: `They get the account back: they can sign in again, and they reappear in leaderboards, member counts and broadcast audiences. Their reviews and recommendations were never touched. One exception this screen cannot see — if they had deleted the account themselves before the ban, it stays deleted, and the result will say so. ${PROPAGATION_NOTICE}`,
    };
  }

  return {
    warns: false,
    text: `They get the account back in full: they can sign in again, and they reappear in leaderboards, member counts and broadcast audiences. Their reviews and recommendations were never touched. ${PROPAGATION_NOTICE}`,
  };
}

/**
 * Trims the reason and drops a blank one.
 *
 * The server already stores `""` as null; sending `undefined` keeps the audit
 * field's two states — a reason, or none — from depending on that.
 */
export function normalizeReason(reason?: string): string | undefined {
  const trimmed = reason?.trim();

  return trimmed ? trimmed : undefined;
}

/**
 * Toast wording for a completed write.
 *
 * The server's own `warning` wins when there is one: it is set for the
 * anonymized cases, where the plain success wording would be wrong in both
 * directions — a ban that locked nothing out, or an un-ban that restored
 * nothing.
 */
export function summarizeBan(status: UserBanStatus, banned: boolean): string {
  if (status.warning) return status.warning;

  if (banned) {
    return `${status.username} is banned — refused on every operation, cannot sign in, and their phone number and email stay reserved so the same details cannot be registered again. ${PROPAGATION_NOTICE}`;
  }

  return `${status.username} is un-banned and can sign in again. ${PROPAGATION_NOTICE}`;
}

/**
 * Toast wording for a failed write.
 *
 * GraphQL errors arrive as HTTP 200 with an `errors` array, so the code comes off
 * `extensions.code` rather than any status. The three documented codes get
 * wording an admin can act on; `BAD_REQUEST` is the self-ban refusal, whose
 * server message already says so.
 */
export function describeBanFailure(
  error: unknown,
  username: string,
  banned: boolean
): string {
  const action = banned ? "ban" : "un-ban";
  const code = graphQLErrorCode(error);

  if (code === "ACCOUNT_NOT_FOUND") {
    return `There is no member with that id, so nobody was ${action}ned. Nothing changed.`;
  }

  if (code === "BAD_REQUEST") {
    // The only BAD_REQUEST this mutation raises is the self-ban refusal, and its
    // message explains itself. Kept generic in case a second one is added.
    return (
      (error as Error | undefined)?.message ??
      `The server refused to ${action} ${username}.`
    );
  }

  if (code === "FORBIDDEN") {
    return `Your account is not allowed to ${action} members. Sign in as an admin and try again.`;
  }

  return describeError(error, `Could not ${action} ${username}`);
}

/** Toast wording for a failed write of the enforcement switch. */
export function describeEnforcementFailure(
  error: unknown,
  enabled: boolean
): string {
  const code = graphQLErrorCode(error);

  if (code === "FORBIDDEN") {
    return "Your account is not allowed to change ban enforcement. Sign in as an admin and try again.";
  }

  return describeError(
    error,
    `Could not turn ban enforcement ${enabled ? "on" : "off"}`
  );
}

function graphQLErrorCode(error: unknown): string | undefined {
  const errors = (
    error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> }
  )?.graphQLErrors;

  const code = errors?.[0]?.extensions?.code;

  return typeof code === "string" ? code : undefined;
}

/* ------------------------------------------------------------------ *
 * The Users list
 * ------------------------------------------------------------------ */

/**
 * The ban cell on the Users screen, which reads a `fetchUsers` row rather than a
 * `bannedUsers` one.
 *
 * Banned members are **not** hidden from `fetchUsers` — it applies no
 * `isDeleted` filter — so the row stays where it was and this is what marks it.
 */
export function banColumnLabel(row: {
  isBanned?: boolean | null;
  bannedAt?: string | null;
}): string {
  if (!row?.isBanned) return "—";

  const when = formatBanTimestamp(row.bannedAt);

  return when ? `Banned ${when}` : "Banned";
}
